import cn from "classnames";
import { useMemo, useState } from "react";

export type BadgeVariant = "new" | "beta";

interface UseFeatureBadgeOptions {
  days?: number; // default 90
}

const useFeatureBadge = (
  _featureKey: string,
  options: UseFeatureBadgeOptions = {},
) => {
  const { days = 90 } = options;

  const [startTs] = useState(() => Date.now());
  const [dismissed, setDismissed] = useState(false);

  const expiresAt = useMemo(
    () => startTs + days * 24 * 60 * 60 * 1000,
    [startTs, days],
  );

  const show = !dismissed && Date.now() <= expiresAt;

  const handleDismiss = () => setDismissed(true);

  return { show, handleDismiss } as const;
};

export interface BadgeProps
  extends Omit<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    "onClick" | "children"
  > {
  variant?: BadgeVariant;
  as?: "span" | "button";
  feature: string;
  expiresInDays?: number;
  onClick?: () => void;
}

export const Badge = ({
  variant = "new",
  as = "span",
  feature,
  expiresInDays,
  onClick,
  ...props
}: BadgeProps) => {
  const { show, handleDismiss } = useFeatureBadge(feature, {
    days: expiresInDays,
  });

  if (!show) {
    return;
  }

  const classNames = cn(
    "inline-block",
    "px-1",
    "py-[2px]",
    "rounded-sm",
    "text-[9px]",
    "font-semibold",
    "text-white",
    "uppercase",
    "bg-indigo",
    "leading-[11px]",
    props.className,
  );

  if (as === "button") {
    return (
      <button
        type="button"
        className={classNames}
        onClick={() => {
          handleDismiss();
          if (onClick) {
            onClick();
          }
        }}
      >
        {variant}
      </button>
    );
  }

  return <span className={classNames}>{variant}</span>;
};
